/*
  <be-personalize-sheet>: mockup 3a (Claude Design turn 3) on phones. Markup and the full
  description: snippets/be-personalize-sheet.liquid. Styles: assets/be-personalize-sheet.css.

  Phones only (max-width: 749px). From 750px the element does nothing visible: the personalizer
  is inline and the Add to bag button keeps its normal label.

  - The row and the Add to bag button open the sheet. Until the shopper taps Done (or arrives
    with engraving text already filled in, e.g. from a metal swatch, the homepage "Try it" box
    or the bag's Edit link), the button reads data-gate-label ("Personalize & add") and
    interceptSubmit() (called by assets/be-product-form.js) opens the sheet instead of adding.
  - Cancel (and Escape) puts back the state from when the sheet opened.
  - Done needs text on at least one side, then shows the "Engraving saved" toast and fills the row.
  - The phone back button closes the sheet (a history entry is pushed while it is open):
    kept like Done when there is text, like Cancel when there is none.
  - ?replace=<cart line key> (the bag's Edit link) opens the sheet on load.
*/
(function () {
  if (!('customElements' in window) || customElements.get('be-personalize-sheet')) return;

  var PHONE = '(max-width: 749px)';
  var TOAST_MS = 3500;
  var MSG_EMPTY = 'Type your engraving first. The front, the back, or both.';
  var MSG_NO_DATE = 'Add your date first.';
  var MSG_NO_NAME = 'Add the name for the corner.';

  function trimmed(t) {
    return String(t || '').trim();
  }

  function hasText(s) {
    return !!s && (!!trimmed(s.front) || !!trimmed(s.back));
  }

  // Ready to add: the personalizer says so (the fixed design needs its date), else any text.
  function isComplete(s) {
    if (!s) return false;
    return typeof s.complete === 'boolean' ? s.complete : hasText(s);
  }

  function isFixed(s) {
    return !!s && /^fixed_/.test(s.design || '');
  }

  function oneLine(t) {
    return String(t || '')
      .split('\n')
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean)
      .join(' / ');
  }

  function fontLabel(value) {
    var BE = window.BEEngraving;
    if (BE && BE.FONT_PREVIEW && BE.FONT_PREVIEW[value]) return BE.FONT_PREVIEW[value].label;
    if (value === 'Script (Cornet)') return 'Script';
    if (value === 'Custom: EB Garamond') return 'Serif';
    if (value && value.indexOf('Custom: ') === 0) return value.slice(8).trim();
    return 'Sans serif';
  }

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  class BEPersonalizeSheet extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.sectionId = this.dataset.section;
      this.row = this.querySelector('[data-pz-open]');
      this.sheet = this.querySelector('[data-pz-sheet]');
      if (!this.row || !this.sheet) return;

      this.scroller = this.querySelector('[data-pz-scroll]');
      this.caption = this.querySelector('[data-pz-caption]');
      this.errorEl = this.querySelector('[data-pz-error]');
      this.titleEl = this.querySelector('[data-pz-title]');
      this.subEl = this.querySelector('[data-pz-sub]');
      this.actionEl = this.querySelector('[data-pz-action]');
      this.toast = this.querySelector('[data-pz-toast]');
      this.thumb = this.row.querySelector('engraving-preview');
      // Free design: one big preview that follows the tab. Fixed design: front and back together.
      this.bigPreviews = this.sheet.querySelectorAll('.be-pz-sheet__preview engraving-preview');
      this.mq = window.matchMedia(PHONE);
      this.isOpen = false;
      this.personalized = false;
      this.snapshot = null;
      this.pushed = false;
      this.opener = null;
      this.baseLabel = null;

      var el = this;
      this.row.addEventListener('click', function () {
        el.open(el.row);
      });
      this.querySelector('[data-pz-cancel]').addEventListener('click', function () {
        el.cancel();
      });
      this.querySelector('[data-pz-done]').addEventListener('click', function () {
        el.done();
      });
      this.querySelector('[data-pz-flip]').addEventListener('click', function () {
        el.flip();
      });

      // Capture phase: an open font list takes Escape first (the personalizer closes it).
      this.sheet.addEventListener(
        'keydown',
        function (e) {
          if (e.key !== 'Escape' || !el.isOpen) return;
          var list = el.sheet.querySelector('[data-font-list]');
          if (list && !list.hidden) return;
          e.preventDefault();
          el.cancel();
        },
        true
      );

      document.addEventListener('engraving:change', function (e) {
        if (e.detail && String(e.detail.sectionId) === String(el.sectionId)) el.sync(e.detail);
      });

      window.addEventListener('popstate', function () {
        if (!el.isOpen || (window.history.state && window.history.state.bePzSheet)) return;
        el.pushed = false;
        el.dismiss();
      });

      var onBreakpoint = function () {
        el.onBreakpoint();
      };
      if (this.mq.addEventListener) this.mq.addEventListener('change', onBreakpoint);
      else if (this.mq.addListener) this.mq.addListener(onBreakpoint);

      // This script runs before the personalizer's (document order), so wait for it.
      customElements.whenDefined('engraving-personalizer').then(function () {
        el.start();
      });
      customElements.whenDefined('engraving-preview').then(function () {
        el.sync(el.getState());
      });
    }

    personalizer() {
      return this.querySelector('engraving-personalizer');
    }

    getState() {
      var p = this.personalizer();
      return p && typeof p.getState === 'function' ? p.getState() : null;
    }

    start() {
      var s = this.getState();
      this.personalized = isComplete(s);
      this.sync(s);
      this.updateCta();

      var editing = false;
      try {
        editing = new URLSearchParams(window.location.search).has('replace');
      } catch (e) {
        /* old browsers: no auto-open */
      }
      if (editing && this.mq.matches && !designMode()) this.open(null);
    }

    isGated() {
      return this.mq.matches && !this.personalized;
    }

    // be-product-form.js calls this before an add. true = the add was turned into "open the sheet".
    interceptSubmit() {
      if (!this.isGated()) return false;
      this.open(document.activeElement);
      return true;
    }

    // ------------------------------------------------------------ open / close
    open(opener) {
      if (this.isOpen || !this.mq.matches) return;
      this.snapshot = this.getState();
      this.opener = opener && typeof opener.focus === 'function' && opener !== document.body ? opener : this.row;
      this.isOpen = true;
      this.setError('');

      this.sheet.setAttribute('role', 'dialog');
      this.sheet.setAttribute('aria-modal', 'true');
      this.sheet.setAttribute('aria-labelledby', 'BePzTitle-' + this.sectionId);
      // Shopify's theme-preview bar (owner previews only) covers the bottom of the screen.
      var bar = document.getElementById('PBarNextFrameWrapper');
      var barH = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      this.sheet.style.setProperty('--be-pz-bar', barH > 0 && barH < 200 ? barH + 'px' : '0px');
      this.sheet.classList.add('is-open');
      this.row.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('be-pz-lock');
      if (this.scroller) this.scroller.scrollTop = 0;
      this.bigPreviews.forEach(function (pv) {
        if (typeof pv.update === 'function') pv.update({});
      });

      if (!designMode() && window.history && window.history.pushState) {
        try {
          window.history.pushState({ bePzSheet: true }, '');
          this.pushed = true;
        } catch (e) {
          this.pushed = false;
        }
      }

      // Focus the dialog itself, never the textarea: the keyboard would cover the preview.
      var sheet = this.sheet;
      window.requestAnimationFrame(function () {
        if (typeof window.trapFocus === 'function') window.trapFocus(sheet);
        else sheet.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      var p = this.personalizer();
      if (p && typeof p.closeFontList === 'function') p.closeFontList();

      this.sheet.classList.remove('is-open');
      this.sheet.removeAttribute('role');
      this.sheet.removeAttribute('aria-modal');
      this.sheet.removeAttribute('aria-labelledby');
      this.row.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('be-pz-lock');

      var back = this.mq.matches ? this.opener : null;
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(back);
      else if (back) back.focus();

      if (this.pushed) {
        this.pushed = false;
        if (window.history.state && window.history.state.bePzSheet) window.history.back();
      }
    }

    cancel() {
      var p = this.personalizer();
      if (this.snapshot && p && typeof p.setState === 'function') p.setState(this.snapshot);
      this.close();
    }

    done() {
      var s = this.getState();
      if (!isComplete(s)) {
        var p = this.personalizer();
        if (isFixed(s)) {
          var needsName = !!s.date && s.design === 'fixed_heart';
          this.setError(needsName ? MSG_NO_NAME : MSG_NO_DATE);
          if (p && typeof p.setSide === 'function') p.setSide('front');
          var field = p && p.querySelector(needsName ? '[data-engraving-name]' : '[data-engraving-date]');
          if (field) field.focus();
          return;
        }
        this.setError(MSG_EMPTY);
        var side = s && s.side === 'back' ? 'back' : 'front';
        var ta = p && p.querySelector('[data-engraving-text="' + side + '"]');
        if (ta) ta.focus();
        return;
      }
      this.personalized = true;
      this.close();
      this.sync(s);
      this.updateCta();
      this.showToast(s);
    }

    // Phone back button while open.
    dismiss() {
      var s = this.getState();
      if (!isComplete(s)) {
        this.cancel();
        return;
      }
      this.personalized = true;
      this.close();
      this.sync(s);
      this.updateCta();
    }

    flip() {
      var p = this.personalizer();
      var s = this.getState();
      if (p && s && typeof p.setSide === 'function') p.setSide(s.side === 'back' ? 'front' : 'back');
    }

    onBreakpoint() {
      var s = this.getState();
      if (!this.mq.matches) {
        // Wider screen: the personalizer is inline now, so keep whatever is typed.
        if (this.isOpen) {
          if (isComplete(s)) this.personalized = true;
          this.close();
        }
      } else if (!this.personalized && isComplete(s)) {
        this.personalized = true;
      }
      this.sync(s);
      this.updateCta();
    }

    // -------------------------------------------------------------- rendering
    sync(s) {
      if (!s) return;
      var side = s.side === 'back' ? 'back' : 'front';

      if (this.caption) {
        this.caption.textContent = (isFixed(s) ? '' : side + ' · ') + this.dataset.metalLabel + ' · preview is a guide, we proof by hand';
      }
      if (this.errorEl && this.errorEl.textContent && isComplete(s)) this.setError('');

      if (this.thumb && typeof this.thumb.update === 'function') {
        this.thumb.update({
          front: s.front,
          back: s.back,
          fontValue: s.fontValue,
          fontFamily: s.fontFamily,
          align: s.align,
          size: s.size,
          side: trimmed(s.front) || !trimmed(s.back) ? 'front' : 'back',
          design: s.design,
          tally: s.tally,
          name: s.name || '',
          heart: s.heart,
        });
      }

      // While the sheet is open the row is covered; it only changes after Done.
      if (this.isOpen) return;
      var done = this.personalized;
      this.row.classList.toggle('is-done', done);
      if (this.titleEl) this.titleEl.textContent = done ? 'Personalized ✓' : 'Personalize';
      if (this.actionEl) this.actionEl.textContent = done ? 'Edit' : 'Start →';
      if (this.subEl) {
        var front = oneLine(s.front);
        var corner = s.design === 'fixed_heart'
          ? (s.heart ? '♡ ' : '') + s.name
          : s.tally + ' tally mark' + (s.tally === 1 ? '' : 's');
        var summary = isFixed(s)
          ? fontLabel(s.fontValue) + ' · ' + s.date + ' · ' + corner
          : fontLabel(s.fontValue) + ' · ' + (front || 'Back: ' + oneLine(s.back));
        this.subEl.textContent = done ? summary : this.dataset.subEmpty || '';
      }
    }

    updateCta() {
      var btn = document.getElementById('ProductSubmitButton-' + this.sectionId);
      if (!btn) return;
      var span = btn.querySelector(':scope > span');
      if (this.baseLabel === null) this.baseLabel = btn.dataset.beLabel || (span ? span.textContent.trim() : '');
      var label = this.isGated() ? this.dataset.gateLabel || 'Personalize & add' : this.baseLabel;
      if (!label) return;
      // be-product-form.js restores data-be-label after variant changes, so keep it current.
      btn.dataset.beLabel = label;
      if (span && !btn.disabled && span.textContent !== label) span.textContent = label;
    }

    setError(text) {
      if (this.errorEl) this.errorEl.textContent = text;
    }

    showToast(s) {
      if (!this.toast) return;
      var front = !!trimmed(s.front);
      var back = !!trimmed(s.back);
      var sides = front && back ? 'Both sides' : front ? 'Front' : 'Back';
      if (isFixed(s)) sides = back ? 'Both sides' : 'Front';
      var title = this.toast.querySelector('[data-pz-toast-title]');
      var meta = this.toast.querySelector('[data-pz-toast-meta]');
      if (title) title.textContent = 'Engraving saved';
      if (meta) meta.textContent = sides + ' · ' + this.dataset.metalLabel;
      this.toast.classList.add('is-on');

      var toast = this.toast;
      window.clearTimeout(this._toastTimer);
      this._toastTimer = window.setTimeout(function () {
        toast.classList.remove('is-on');
        window.setTimeout(function () {
          if (toast.classList.contains('is-on')) return;
          if (title) title.textContent = '';
          if (meta) meta.textContent = '';
        }, 300);
      }, TOAST_MS);
    }
  }

  customElements.define('be-personalize-sheet', BEPersonalizeSheet);
})();
